import * as am5 from '@amcharts/amcharts5';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5locales_fr_FR from "@amcharts/amcharts5/locales/fr_FR";
import am5locales_es_ES from "@amcharts/amcharts5/locales/es_ES";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import * as am5xy from '@amcharts/amcharts5/xy';
import { Box, CircularProgress, useTheme } from '@mui/material';
import { getGraphPalette, graphLineAlarm } from 'constants/ColorsForGraph';
import { MeasurementType } from 'constants/MeasurementType';
import { Periodicity } from 'constants/Periodicity';
import { forEach, get, isEmpty, map, toLower } from 'lodash';
import moment from 'moment';
import { useLayoutEffect } from 'react';
import { getLabelFromMeasurer, getFullLabelFromMeasurer } from 'utils/MeasurerUtils';
import { SCREEN_HEIGHT } from '../../constants/ScreenHeight';
import useScreenHeight from '../../hooks/useScreenHeight';
import { handleUnit } from 'utils/UnitsConverter';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { graphPaletteKey } from 'atoms/graphPaletteAtom';

const CHART_ID = 'data_chart';

// Neutral color used for an axis shared by several curves.
const sharedAxisColor = 0x555555;

const isInversedAxis = (measurement: any): boolean =>
  get(measurement, 'measurer.measurementType') === 'WATER_DEPTH_RELATIVE';

const convertPeriodToTimeStamp = (period: number) => {
  const strPeriod = '' + period;
  let dateStr = '';
  if(strPeriod.length === 6){
    dateStr = `${strPeriod.substring(0, 4)}W${strPeriod.substring(4)}`;
  }
  else{
    dateStr = `${strPeriod.substring(0, 8)}T${strPeriod.substring(8)}00`;
  }
  return moment.utc(dateStr).toDate().getTime();
};

const formatDateOption:Intl.DateTimeFormatOptions = {
  dateStyle:'short',
  timeStyle:'short',
}

function languageToLocale(language:string):string{
  if(language === 'fr' || language === 'fr-FR') return 'fr-FR';
  if(language === 'es'  || language === 'es-ES') return 'es-ES';
  return 'en-US';
}

// Creates a value axis shared by every series using the same unit.
// `color` is the axis color: the series color when the axis is dedicated to a
// single series, a neutral color when several series share it.
function createValueAxis(
  root: am5.Root,
  chart: am5xy.XYChart,
  opposite: boolean,
  inversed: boolean,
  label: string,
  color: any,
) {
  let yRenderer = am5xy.AxisRendererY.new(root, {
    opposite: opposite,
    inversed: inversed,
  });
  let yAxis = chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: yRenderer,
    }),
  );

  const axisLabel = am5.Label.new(root, {
    text: label,
    textAlign: 'center',
    fill: color,
    y: am5.p50,
    rotation: -90,
    fontWeight: 'bold',
  });
  if (opposite) {
    yAxis.children.push(axisLabel);
  } else {
    yAxis.children.unshift(axisLabel);
  }

  yRenderer.grid.template.set('strokeOpacity', 0.05);
  yRenderer.labels.template.set('fill', color);
  yRenderer.setAll({
    stroke: color,
    strokeOpacity: 1,
    opacity: 1,
  });

  return yAxis;
}

function createSeries(
  root: am5.Root,
  chart: am5xy.XYChart,
  xAxis: any,
  yAxis: any,
  valueYField: string,
  dataSet: any,
  color: any,
  name: string,
  tagName: string,
  isBarChart: boolean,
) {
  // Add series -> line
  // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
  let tooltip = am5.Tooltip.new(root, {
    pointerOrientation: 'horizontal',
  });
  tooltip.label.setAll({
    text: tagName + '\n{formattedDate} : {valueYFormatted} {unit}',
  });

  const commonSettings = {
    xAxis: xAxis,
    yAxis: yAxis,
    name,
    valueYField: valueYField,
    valueXField: 'date',
    legendValueText: '{valueYFormatted}',
    fill: color,
    tooltip: tooltip,
  };

  let series;
  if (isBarChart) {
    series = chart.series.push(am5xy.ColumnSeries.new(root, commonSettings));
    (series as am5xy.ColumnSeries).columns.template.setAll({
      strokeOpacity: 0,
      fillOpacity: 0.8,
      width: am5.percent(90),
      cornerRadiusTL: 2,
      cornerRadiusTR: 2,
    });
  } else {
    series = chart.series.push(am5xy.LineSeries.new(root, { ...commonSettings, stroke: color }));
    (series as am5xy.LineSeries).strokes.template.setAll({ strokeWidth: 1 });
  }
  series.data.setAll(dataSet);

  return series;
}

function createRange(item: any, yAxis: any) {
  const alarmColor = am5.color(graphLineAlarm);
  if (item) {
    let range = yAxis.createAxisRange(
      yAxis.makeDataItem({
        value: item.thresholdValue.fieldValue,
      }),
    );
    if (range !== undefined) {
      range.get('grid')!.setAll({
        forceHidden: false,
        strokeOpacity: 1,
        strokeWidth: 1,
        stroke: alarmColor,
      });
    }
  }
}

const getTimeUnitFromPeriodicity = (periodicity: Periodicity): any => {
  return toLower(periodicity);
};

const getAmChartsLocaleFromLanguage = (usedLanguage:string) => {
  console.log('Language', usedLanguage);
  if(usedLanguage === 'fr' || usedLanguage === 'fr-FR') return am5locales_fr_FR;
  if(usedLanguage === 'es' || usedLanguage === 'es-ES') return am5locales_es_ES;
  return am5locales_en_US;

}

export default function GraphAMCharts({
  measurements,
  measurementList,
  alarmTriggerList,
  periodicity,
  loading = false,
  t,
}: any) {
  const theme = useTheme();
  const screenHeight = useScreenHeight();
  const { i18n } = useTranslation();
  const usedLanguage = i18n.language;
  const selectedPalette = useRecoilValue(graphPaletteKey);
  const colorPalette = getGraphPalette(selectedPalette);

  const graphHeight = measurements
    ? screenHeight - SCREEN_HEIGHT.HEADER - 150
    : (screenHeight - SCREEN_HEIGHT.HEADER) / 2 - 80;

  useLayoutEffect(() => {
    let root = am5.Root.new(CHART_ID);
    root.locale = getAmChartsLocaleFromLanguage(usedLanguage);
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomY',
        layout: root.verticalLayout,
      }),
    );

    // Create Xaxes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    let xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: {
          timeUnit: getTimeUnitFromPeriodicity(periodicity),
          count: 1,
        },
        renderer: am5xy.AxisRendererX.new(root, {}),
      }),
    );

    // Add export menu
    am5plugins_exporting.Exporting.new(root, {
      menu: am5plugins_exporting.ExportingMenu.new(root, {}),
    });

    // Add cursor
    // https://www.amcharts.com/docs/v5/charts/xy-chart/cursor/
    let cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        behavior: 'zoomY',
      }),
    );
    cursor.lineX.set('visible', false);

    // Curves measuring the exact same thing — same measurement type, same unit,
    // same axis direction — share a single Y axis so they are comparable.
    // A shared unit alone is not enough: two different quantities can be in
    // meters without being comparable.
    const axisGroupKey = (measurement: any): string => {
      const measurementType = get(measurement, 'measurer.measurementType');
      const unit = handleUnit(get(measurement, 'measurer.measurementUnit'));
      return `${measurementType}|${unit}|${isInversedAxis(measurement)}`;
    };

    const groupedIndexes: { [key: string]: number[] } = {};
    forEach(measurementList, (measurement, key) => {
      const groupKey = axisGroupKey(measurement);
      groupedIndexes[groupKey] = [...(groupedIndexes[groupKey] || []), Number(key)];
    });

    // A shared axis is named after the measurement type its curves have in
    // common, not after any single curve's custom label.
    const sharedAxisLabel = (measurement: any): string => {
      const unit = handleUnit(get(measurement, 'measurer.measurementUnit'));
      const typeLabel = t(
        `measurementType.${toLower(get(measurement, 'measurer.measurementType', ''))}`,
      );
      return isEmpty(unit) ? typeLabel : `${typeLabel} (${unit})`;
    };

    const yAxisByGroup: { [key: string]: any } = {};
    let axisCount = 0;

    forEach(measurementList, (measurement, key) => {
      const index = Number(key);
      const tagName = getFullLabelFromMeasurer(measurement.measurer, t);
      const mapToMeasurement = map(measurement.data, (measurementItem) => {
        return {
          ...measurementItem,
          date: convertPeriodToTimeStamp(measurementItem.period),
          formattedDate: new Date(convertPeriodToTimeStamp(measurementItem.period)).toLocaleString(languageToLocale(usedLanguage), formatDateOption),
          unit: handleUnit(measurementItem.measurementUnit),
          valueYFormatted: parseFloat((measurementItem.averageValue || 0).toFixed(2)),
          tagName: tagName
        };
      });
      const measurementType = get(measurement, 'measurer.measurementType');
      const isBarChart = measurementType === MeasurementType.RAIN;
      const groupKey = axisGroupKey(measurement);
      const isSharedAxis = groupedIndexes[groupKey].length > 1;

      let yAxis = yAxisByGroup[groupKey];
      if (!yAxis) {
        // A shared axis belongs to no single curve, so it stays neutral instead
        // of taking one of the curves' colors.
        yAxis = createValueAxis(
          root,
          chart,
          axisCount % 2 === 1,
          isInversedAxis(measurement),
          isSharedAxis
            ? sharedAxisLabel(measurement)
            : getLabelFromMeasurer(measurement.measurer, t),
          isSharedAxis
            ? am5.color(sharedAxisColor)
            : colorPalette[index],
        );
        yAxisByGroup[groupKey] = yAxis;
        axisCount++;
      }

      createSeries(
        root,
        chart,
        xAxis,
        yAxis,
        'averageValue',
        mapToMeasurement,
        colorPalette[index],
        getLabelFromMeasurer(measurement.measurer, t),
        tagName,
        isBarChart,
      );

      const alarmTriggers = get(alarmTriggerList, index);
      if (alarmTriggers) {
        forEach(alarmTriggers, (item: any) => createRange(item, yAxis));
      }
    });

    let legend = chart.children.push(
      am5.Legend.new(root, {
        nameField: 'name',
        fillField: 'color',
        strokeField: 'color',
        centerX: am5.percent(0),
        x: am5.percent(0),
        y: am5.percent(95),
        marginBottom: 10,
      }),
    );

    legend.data.setAll(
      map(measurementList, (measurement, key) => {
        const index = Number(key);
        return {
          name: getFullLabelFromMeasurer(measurement.measurer, t),
          color: colorPalette[index],
        };
      }),
    );

    chart.set(
      'scrollbarX',
      am5.Scrollbar.new(root, {
        orientation: 'horizontal',
        y: am5.percent(100),
        dy: -20,
        centerY: am5.percent(100),
      }),
    );

    return () => {
      root.dispose();
    };
  }, [measurementList, alarmTriggerList, usedLanguage, colorPalette]);

  return (
    <div
      style={{ position: 'relative', margin: `${theme.spacing(2)}px 0` }}
    >
      <div
        id={CHART_ID}
        style={{
          width: '100%',
          height: graphHeight,
        }}
      />
      {/* The chart container must stay mounted for amCharts, so the loader
          is overlaid on top of it rather than replacing it. */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </div>
  );
}
